import { put, call } from 'redux-saga/effects'
import * as types from '../../../configs/constants/actionType'
import generateActions from '../../actions/actions'
import axios from '../../../configs/axios/axios'
import { AxiosRequestConfig } from 'axios'
import { ERROR_CODE } from '../../../configs/constants/eaiLicenses'
const ENDPOINTS = '/import-licenses'

interface Action {
  type: string
  params: {
    page: number
    size: number
    objectParams: any
  }
}

const getDataByPage = (page?: number, size?: number, objectParams?: object) => {
  const pageR = page !== undefined && page > 0 ? page - 1 : 0

  const body = {
    page: pageR,
    size,
    ...objectParams
  }

  return axios.post(`${ENDPOINTS}/search`, body).then((res) => res.data)
}

export function* eaiLicenseListSagaByPage(action: Action) {
  try {
    if (action.params.objectParams) {
      Object.keys(action.params.objectParams).forEach((ele: any) => {
        if (typeof action.params.objectParams[ele] == 'string') {
          action.params.objectParams[ele] = action.params.objectParams[ele].trim()
        }
      })
    }
    const data = yield call(() =>
      getDataByPage(action.params.page, action.params.size, action.params.objectParams).then(
        (res: any) => res
      )
    )
    yield put({
      type: generateActions(types.EAI_LICENSE_LIST).SUCCESS,
      payload: { ...data.data }
    })
  } catch (err) {
    yield put({ type: generateActions(types.EAI_LICENSE_LIST).FAILURE })
    Error(err)
  }
}

const getValueDropdown = () => {
  return axios.get('import-licenses/search-resources').then((res) => res.data)
}

export function* getValueDropdownSaga(action: any) {
  try {
    const dropdownValue = yield call(() => getValueDropdown().then((res: any) => res))
    yield put({
      type: generateActions(types.EAI_LICENSE_DROPDOWN).SUCCESS,
      payload: {
        usbSn: dropdownValue.data.usbSNs
      }
    })
  } catch (err) {
    Error(err)
  }
}

function _makingChunks(file: any) {
  // chunk option
  const chunkSize = 5 * 1024 * 1024 // 5mb each
  const chunks = []
  let start = 0
  let end = chunkSize

  while (start < file.size) {
    chunks.push(file.slice(start, end))
    start = end
    end = end + chunkSize
  }
  return { chunks: chunks, totalChunks: chunks.length }
}

function _uploadChunk(
  chunk: any,
  chunkNumber: number,
  totalChunks: number,
  folderName: string,
  originalName: string
) {
  const formData = new FormData()
  formData.append('files', chunk)
  formData.append('chunkNumber', `${chunkNumber}`)
  formData.append('totalChunks', `${totalChunks}`)
  formData.append('folderName', `${folderName}`)
  formData.append('originalName', originalName)
  return axios.post(`${ENDPOINTS}/upload`, formData)
}

function* _uploadFile(file: any, folderName: string) {
  const { chunks, totalChunks } = _makingChunks(file)
  for (let i = 0; i < totalChunks; i++) {
    try {
      yield _uploadChunk(chunks[i], i, totalChunks, folderName, file.name)
    } catch (error) {
      throw error
    }
  }
  return totalChunks
}

function _eaiLicenseImport(originalName: string, folderName: string, type: string) {
  const formData = new FormData()
  formData.append('originalName', originalName)
  formData.append('folderName', folderName)
  formData.append('type', type)
  return axios.post(`${ENDPOINTS}`, formData)
}

export function* eaiLicenseImportSaga(action: any) {
  const { files, folderName, type, onSuccess, onError } = action.data
  try {
    yield call(() => _uploadFile(files[0], folderName))
    yield call(() => _eaiLicenseImport(files[0].name, folderName, type))
    yield put({
      type: generateActions(types.EAI_LICENSE_IMPORT).SUCCESS
    })
    if (onSuccess) yield call(onSuccess)
  } catch (err) {
    const error = err as any
    const errorCode =
      error?.response?.data?.error?.details?.code || ERROR_CODE.IMPORT_LICENSE_NETWORK_ERROR

    yield put({
      type: generateActions(types.EAI_LICENSE_IMPORT).FAILURE
    })
    if (onError) {
      yield call(onError, errorCode)
    }

    yield put({
      type: generateActions(types.EAI_LICENSE_IMPORT).FAILURE,
      payload: {
        responseStatus: {
          status: 'error',
          errorCode: errorCode
        }
      }
    })
  }
}

const deactivateEAILicenseAPI = (ids: number[]) =>
  axios.post('import-licenses/deactivate', { ids }).then((res) => res.data)

export function* deactivateEAILicenseSaga(action: any) {
  const { ids, onSuccess, onError } = action.data
  try {
    yield call(() => deactivateEAILicenseAPI(ids))
    yield put({
      type: generateActions(types.DEACTIVATE_EAI_LICENSE).SUCCESS,
      ids,
      payload: {
        responseStatus: {
          msg: 'deactivated_successfully',
          status: 'success',
          field: 'deactivatedEAI'
        }
      }
    })
    if (onSuccess) yield call(onSuccess)
  } catch (err) {
    const error = err as any
    const errorCode =
      error?.response?.data?.error?.details?.code || ERROR_CODE.DEACTIVATE_LICENSE_NETWORK_ERROR

    yield put({
      type: generateActions(types.DEACTIVATE_EAI_LICENSE).FAILURE,
      payload: {
        responseStatus: {
          msg: 'deactivated_fail',
          status: 'error',
          field: 'deactivatedEAI'
        }
      }
    })
    if (onError) yield call(onError, errorCode)
  }
}

const syncEAILicensesAPI = () => axios.post('import-licenses/sync-cryptlex').then((res) => res.data)

export function* syncEAILicensesSaga(action: any) {
  const { onSuccess, onError } = action.data
  try {
    const result = yield call(() => syncEAILicensesAPI())
    yield put({
      type: generateActions(types.SYNC_EAI_LICENSE).SUCCESS,
      payload: {
        responseStatus: {
          msg: 'sync_successfully',
          status: 'success',
          field: 'syncEAI',
          updated: result.data?.updated || 0
        }
      }
    })
    if (onSuccess) yield call(onSuccess)
  } catch (err) {
    yield put({
      type: generateActions(types.SYNC_EAI_LICENSE).FAILURE,
      payload: {
        responseStatus: {
          msg: 'sync_failed',
          status: 'error',
          field: 'syncEAI'
        }
      }
    })
    if (onError) yield call(onError)
  }
}
